import React, { useState, useEffect } from 'react'
import { signOut, getCurrentUser } from '../lib/supabase'
import { getSessionHeaders } from '../lib/serverSession'
import { logNavigation } from '../lib/personalActivityLogger'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
import { checkAdminStatus } from '../lib/adminAuth'
import { useAdminNotifications, getBadgeCountForTab } from '../hooks/useAdminNotifications'
// import { ContentEditor } from './ContentEditor'
// import { MiningDataEditor } from './MiningDataEditor'
import { GalleryManager } from './admin/GalleryManager'
import { DatabaseTest } from './admin/DatabaseTest'
import { SponsorManager } from './admin/SponsorManager'
import { PaymentManager } from './admin/PaymentManager'
import { UserManager } from './admin/UserManager'
import DormantAccountPurge from './admin/DormantAccountPurge'
import AccountSuspensionDesk from './admin/AccountSuspensionDesk'
import ProfileKycAlignmentDesk from './admin/ProfileKycAlignmentDesk'
import { AuditLogViewer } from './admin/AuditLogViewer'
import { MarketingMaterialsManager } from './admin/MarketingMaterialsManager'
import CertificateManagement from './admin/CertificateManagement'
import CertificateTestSystem from './admin/CertificateTestSystem'
import { AdminWalletManager } from './admin/AdminWalletManager'
import { TestUserAuditReport } from './admin/TestUserAuditReport'
import { FundManagementDashboard } from './admin/FundManagementDashboard'
// v4.7.52 — DownlineManager sidebar tab removed (rendered slowly, redundant with Master Audit). Component file kept on disk for future reuse.
// import { DownlineManager } from './admin/DownlineManager'
import PhaseCompetitionManager from './admin/PhaseCompetitionManager'
import { AdminGoldDiggers } from './admin/AdminGoldDiggers'
import { AdminPerformancePoolPayouts } from './admin/AdminPerformancePoolPayouts'
import CompetitionWinnersManager from './admin/CompetitionWinnersManager'
import ComprehensiveSupportDashboard from './admin/ComprehensiveSupportDashboard'
import CommissionWithdrawalManager from './admin/CommissionWithdrawalManager'
import { CommissionConversionManager } from './admin/CommissionConversionManager'
import { TrainingAcademyManager } from './admin/TrainingAcademyManager'
import MeetingSchedulerManager from './admin/MeetingSchedulerManager'
import AnnouncementManager from './admin/AnnouncementManager'
import AnnouncementPopup from './user/AnnouncementPopup'
import ListManagerDashboard from './admin/ListManagerDashboard'
import ShareLedgerReportSystem from './admin/ShareLedgerReportSystem'
import { TransactionManagementSystem } from './admin/TransactionManagementSystem'
import '../styles/TransactionManagementSystem.css'
import KYCManagementDashboard from './admin/KYCManagementDashboard'
import CommissionAuditDashboard from './admin/CommissionAuditDashboard'
import WithdrawalManagementDashboard from './admin/WithdrawalManagementDashboard'
import KYCCleanupDashboard from './admin/KYCCleanupDashboard'
import AdminKYCCompletion from './admin/AdminKYCCompletion'
import FinancialAuditReportGenerator from './admin/FinancialAuditReportGenerator'
import { UsernameAuditDashboard } from './admin/UsernameAuditDashboard'
import ShareBalanceAuditDashboard from './admin/ShareBalanceAuditDashboard'
import PaymentIntegrityAudit from './admin/PaymentIntegrityAudit'
import PaymentIntegrityAuditV2 from './admin/PaymentIntegrityAuditV2'
import ReversalHistory from './admin/ReversalHistory'
import { MassEmailBroadcast } from './admin/MassEmailBroadcast'
import { MenuOrderManager } from './admin/MenuOrderManager'
import { MasterAuditEnhanced } from './admin/MasterAuditEnhanced'
import { MasterAuditModeChooser, type MasterAuditHubChoice } from './admin/MasterAuditModeChooser'
import { RemitanoDepositAuditPanel } from './admin/RemitanoDepositAuditPanel'
import { CountryShareAuditPanel } from './admin/CountryShareAuditPanel'
import AdminUserManager from './admin/AdminUserManager'
import AdminRolePermissionManager from './admin/AdminRolePermissionManager'
import UserAssistance from './admin/UserAssistance'
import AdminSecurity from './admin/AdminSecurity'
import { getAccessiblePages, PAGE_PERMISSIONS } from '../lib/adminPermissions'
import { FoundingShareholdersManager } from './admin/FoundingShareholdersManager'
import AdminTodoDashboard from './admin/AdminTodoDashboard'
import { AdminHowToStartManager } from './AdminHowToStartManager'
import PhaseTransitionManager from './admin/PhaseTransitionManager'
import { MaintenanceModeManager } from './admin/MaintenanceModeManager'
import { SystemSettingsManager } from './admin/SystemSettingsManager'
import { TeamManager } from './admin/TeamManager'
import { CommissionCascadeAudit } from './admin/CommissionCascadeAudit'
import { SessionManager } from './admin/SessionManager'
import CountryManager from './admin/CountryManager'
import { InvoiceManager } from './admin/InvoiceManager'
import { RevenueAllocationReport } from './admin/RevenueAllocationReport'
import { CompanyManager } from './admin/CompanyManager'
import { RoadmapJournalManager } from './admin/RoadmapJournalManager'
import SecurityAdminDashboard from './admin/SecurityAdminDashboard'
import LoginLockoutManager from './admin/LoginLockoutManager'
import { LockedWalletAdmin } from './admin/LockedWalletAdmin'
import { WhatsAppOnboarding } from './admin/WhatsAppOnboarding'
import { TopupApprovalManager } from './admin/TopupApprovalManager'
import AdminShareTransfer from './admin/AdminShareTransfer'
import AcresDashboard from './admin/AcresDashboard'
import { extractNumericId } from '../lib/userIdExtractor'

interface AdminDashboardProps {
  onLogout: () => void
  user?: any
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout, user: propUser }) => {
  // Parse URL hash to determine initial tab (e.g., #users or #users&search=username)
  const getInitialTab = () => {
    try {
      const hash = window.location.hash
      if (hash && hash.length > 1) {
        // Remove the # and get the first part (before any &)
        const tabPart = hash.substring(1).split('&')[0]
        if (tabPart) {
          return tabPart
        }
      }
    } catch (e) {
      console.error('Error parsing URL hash:', e)
    }
    return 'users'
  }

  const [activeTab, setActiveTab] = useState(getInitialTab())
  const [masterAuditHubMode, setMasterAuditHubMode] = useState<MasterAuditHubChoice | null>(null)
  const [showMasterAuditChooser, setShowMasterAuditChooser] = useState(() => getInitialTab() === 'master-audit')
  const [user, setUser] = useState<any>(propUser || null)
  const [loading, setLoading] = useState(true)
  const [adminVerified, setAdminVerified] = useState(false)
  const [customMenuOrder, setCustomMenuOrder] = useState<string[]>([])
  const [disabledTabs, setDisabledTabs] = useState<string[]>([])
  const [menuParents, setMenuParents] = useState<Record<string, string | null>>({})
  const [expandedParents, setExpandedParents] = useState<string[]>([])
  const [accessiblePages, setAccessiblePages] = useState<string[]>([])
  const { counts: notificationCounts } = useAdminNotifications()

  useEffect(() => {
    loadUser()
  }, [])

  useEffect(() => {
    if (user?.email) {
      loadCustomMenuOrder()
      loadAccessiblePages()
    }
  }, [user?.email])

  // Personal Activity Report — log admin tab navigation. Server-side flag
  // gates the actual write; disabled admins skip the network round-trip via
  // the logger's cached enabled flag. Touches no audit/financial ledgers.
  useEffect(() => {
    if (!activeTab || !user?.email) return
    logNavigation(activeTab, activeTab, typeof window !== 'undefined' ? window.location.hash || window.location.pathname : undefined)
  }, [activeTab, user?.email])

  const loadAccessiblePages = async () => {
    if (!user?.email) return

    // Super admins get access to ALL pages - no need to check individual permissions
    if (user?.adminUser?.role === 'super_admin') {
      const allPageIds = defaultTabs.map(tab => tab.id)
      setAccessiblePages(allPageIds)
      console.log('✅ Super admin - all pages accessible:', allPageIds.length)
      return
    }

    try {
      // Get role-based permissions
      const rolePages = await getAccessiblePages(user.email)

      // Also check if this user has a support_agents record with specific page_access
      let agentPages: string[] = []
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'get_agent_pages', data: { email: user.email } })
        })
        const result = await response.json()
        if (response.ok && Array.isArray(result.data)) {
          agentPages = result.data
          console.log('✅ Agent page_access loaded:', agentPages)
        }
      } catch (e) {
        console.warn('Could not load agent page_access:', e)
      }

      // Merge both: role-based + agent-specific pages
      const merged = [...new Set([...rolePages, ...agentPages])]
      setAccessiblePages(merged)
      console.log('✅ Accessible pages loaded (merged):', merged)
    } catch (error) {
      console.error('Error loading accessible pages:', error)
    }
  }

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser()

      // 🔒 CRITICAL SECURITY CHECK: Verify admin status
      if (currentUser?.email) {
        console.log('🔍 AdminDashboard: Verifying admin status for:', currentUser.email)
        const adminUser = await checkAdminStatus(currentUser.email)

        if (!adminUser) {
          console.log('❌ AdminDashboard: Access denied - user is not an admin')
          alert('Access denied. You do not have admin privileges.')
          onLogout()
          return
        }

        console.log('✅ AdminDashboard: Admin verified:', adminUser.email, adminUser.role)
        setUser({ ...currentUser, adminUser })
        setAdminVerified(true)
      } else {
        console.log('❌ AdminDashboard: No user found')
        onLogout()
        return
      }
    } catch (error) {
      console.error('Error loading user:', error)
      onLogout()
    } finally {
      setLoading(false)
    }
  }

  const loadCustomMenuOrder = async () => {
    try {
      console.log('📋 [ADMIN-DASHBOARD] Loading custom menu order for:', user?.email)

      const response = await fetch(`${API_BASE_URL}/api/admin/menu-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_menu_order', data: { adminEmail: user?.email } })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ [ADMIN-DASHBOARD] Error loading menu order:', result.error)
        return
      }

      const data = result.data

      if (data?.menuOrder) {
        console.log('✅ [ADMIN-DASHBOARD] Loaded custom menu order:', data.menuOrder)
        setCustomMenuOrder(data.menuOrder as string[])
      } else {
        console.log('ℹ️ [ADMIN-DASHBOARD] No custom menu order found, using default')
      }

      if (data?.disabledItems) {
        setDisabledTabs(data.disabledItems as string[])
      } else {
        setDisabledTabs([])
      }

      if (data?.menuParents) {
        setMenuParents(data.menuParents as Record<string, string | null>)
      } else {
        setMenuParents({})
      }
    } catch (error) {
      console.error('❌ [ADMIN-DASHBOARD] Error loading menu order:', error)
    }
  }

  const handleMenuOrderChange = (newOrder: string[]) => {
    console.log('📋 [ADMIN-DASHBOARD] Menu order changed:', newOrder)
    setCustomMenuOrder(newOrder)
  }

  const handleMenuConfigChange = (config: {
    order: string[]
    disabledItems: string[]
    menuParents: Record<string, string | null>
  }) => {
    console.log('📋 [ADMIN-DASHBOARD] Menu config changed:', config)
    setCustomMenuOrder(config.order)
    setDisabledTabs(config.disabledItems)
    setMenuParents(config.menuParents)
  }

  const handleLogout = async () => {
    await signOut()
    onLogout()
  }

  // v4.7.78: ACRES Engagement tab is restricted to user 106 again while
  // pre-rollout testing continues. The v4.7.69 RBAC-only rollout has been
  // rolled back to the v4.7.68 user-106 gate. RBAC still applies on top of the
  // 106 gate (see the tabs filter below); server-side ACRES admin endpoints
  // continue to enforce manage_acres_* permissions independently.
  const adminNumericId = extractNumericId(user?.database_user?.id || user?.id)
  const isAcresTestUser = adminNumericId === 106
  const defaultTabs = [
    { id: 'todo', name: 'Task Dashboard', icon: '📋' },
    { id: 'system-settings', name: 'System Settings', icon: '⚙️' },
    { id: 'maintenance', name: 'Maintenance Mode', icon: '🔧' },
    { id: 'phase-transition', name: 'Phase Transition', icon: '🔄' },
    { id: 'users', name: 'User Management', icon: '👥' },
    { id: 'dormant-purge', name: 'Dormant Accounts', icon: '🧹' },
    { id: 'account-suspensions', name: 'Account Suspensions', icon: '⛔' },
    { id: 'whatsapp-onboarding', name: 'WhatsApp Onboarding', icon: '📱' },
    { id: 'kyc', name: 'KYC Management', icon: '🔍' },
    { id: 'profile-kyc-alignment', name: 'Profile / KYC Match', icon: '🪪' },
    { id: 'company-accounts', name: 'Company Accounts', icon: '🏢' },
    { id: 'admin-kyc-completion', name: 'Admin KYC Completion', icon: '📝' },
    { id: 'kyc-cleanup', name: 'KYC Cleanup', icon: '🧹' },
    { id: 'payments', name: 'Payment Management', icon: '💰' },
    { id: 'payment-integrity', name: 'Payment Integrity Audit (OLD)', icon: '🔍' },
    { id: 'payment-integrity-v2', name: 'Payment Integrity Audit V2', icon: '💎' },
    { id: 'reversal-history', name: 'Reversal History', icon: '🔄' },
    { id: 'topup-approval', name: 'Top-up Approvals', icon: '💵' },
    { id: 'share-transfer', name: 'Share Transfer', icon: '🔁' },
    { id: 'commissions', name: 'Commission & Withdrawals', icon: '💸' },
    { id: 'withdrawal-management', name: 'Withdrawal Management', icon: '💰' },
    { id: 'commission-conversions', name: 'Commission Conversions', icon: '🔄' },
    { id: 'commission-audit', name: 'Commission Audit', icon: '🔍' },
    { id: 'commission-cascade-audit', name: 'Commission Cascade Audit', icon: '🔗' },
    { id: 'share-balance-audit', name: 'Share Balance Audit', icon: '⚖️' },
    { id: 'username-audit', name: 'Username Audit', icon: '🔧' },
    { id: 'master-audit', name: 'Master Audit', icon: '📊' },
    { id: 'financial-audit', name: 'Financial Audit Reports', icon: '📊' },
    { id: 'invoices', name: 'Legal Invoices', icon: '📄' },
    { id: 'revenue-allocation', name: 'Revenue Allocation', icon: '📊' },
    { id: 'reports', name: 'Share Ledger Reports', icon: '📊' },
    { id: 'transaction-management', name: 'Transaction Management', icon: '🔄' },
    { id: 'funds', name: 'Fund Management', icon: '💵' },
    { id: 'wallets', name: 'Wallet Management', icon: '🏦' },
    { id: 'certificates', name: 'Certificate Management', icon: '📜' },
    { id: 'cert-test', name: 'Certificate Test System', icon: '🧪' },
    { id: 'sponsors', name: 'Sponsor Management', icon: '🤝' },
    // v4.7.52 — Downline Management tab removed for performance; data still visible via Master Audit user investigation.
    { id: 'gold-diggers-admin', name: 'Gold Diggers Management', icon: '⛏️' },
    { id: 'gold-diggers-payouts', name: 'Pool Payouts', icon: '💰' },
    { id: 'competitions', name: 'Competition Management', icon: '🏆' },
    { id: 'competition-winners', name: 'Competition Winners', icon: '🏆' },
    { id: 'founding-shareholders', name: 'Founding Shareholders', icon: '🏆' },
    { id: 'team-management', name: 'Team Management', icon: '👥' },
    { id: 'meetings', name: 'Meeting Scheduler', icon: '📅' },
    { id: 'announcements', name: 'Announcements', icon: '📢' },
    { id: 'gallery', name: 'Gallery Management', icon: '📸' },
    { id: 'roadmap-journal', name: 'Roadmap Journal', icon: '📖' },
    { id: 'marketing', name: 'Marketing Materials', icon: '📁' },
    { id: 'how-to-start', name: 'Training Videos', icon: '🎥' },
    { id: 'list-manager', name: 'List Manager', icon: '📧' },
    { id: 'country-manager', name: 'Country Manager', icon: '🌍' },
    { id: 'mass-email', name: 'Mass Email Broadcast', icon: '📧' },
    { id: 'menu-order', name: 'Menu Order', icon: '📋' },
    { id: 'audit', name: 'Audit Logs', icon: '📋' },
    { id: 'admin-users', name: 'Admin Users', icon: '👨‍💼' },
    { id: 'role-permissions', name: 'Roles & Permissions', icon: '🔐' },
    { id: 'user-assistance', name: 'User Assistance', icon: '🆘' },
    { id: 'login-lockouts', name: 'Login Lockouts', icon: '⏱️' },
    { id: 'my-security', name: 'My Security (MFA)', icon: '🔑' },
    { id: 'session-manager', name: 'Session Manager', icon: '🔒' },
    { id: 'locked-wallets', name: 'Locked Wallets', icon: '🔐' },
    { id: 'security-admin', name: 'Security Dashboard', icon: '🛡️' },
    { id: 'acres', name: 'ACRES Engagement', icon: '🎁' },
    { id: 'support', name: 'Support Dashboard', icon: '🆘' },
    { id: 'training', name: 'Training Academy', icon: '🎓' },
    { id: 'test-audit', name: 'Test User Audit', icon: '🔍' },
    { id: 'phase-test', name: 'Phase Commission Test', icon: '🧪' },
    { id: 'debug', name: 'Debug System', icon: '🐛' }
  ]

  // Apply custom menu order if available, then filter by accessible pages
  const orderedTabs = customMenuOrder.length > 0
    ? customMenuOrder
        .map(id => defaultTabs.find(tab => tab.id === id))
        .filter(Boolean)
        .concat(defaultTabs.filter(tab => !customMenuOrder.includes(tab.id)))
    : defaultTabs

  // Filter tabs based on user permissions and disabled state
  // IMPORTANT: Pages NOT in PAGE_PERMISSIONS are accessible to ALL admins
  const tabs = accessiblePages.length > 0
    ? orderedTabs.filter(tab => {
        if (!tab) return false
        // Always show menu-order to all admins so they can recover if they hide things
        if (tab.id === 'menu-order') return true
        // Hide disabled tabs from navigation
        if (disabledTabs.includes(tab.id)) return false
        // v4.7.78: ACRES Engagement tab gated to user 106 during pre-rollout testing.
        if (tab.id === 'acres' && !isAcresTestUser) return false
        // If page is NOT in PAGE_PERMISSIONS, it's accessible to all admins
        if (!PAGE_PERMISSIONS[tab.id]) return true
        // If page IS in PAGE_PERMISSIONS, check if user has access
        return accessiblePages.includes(tab.id)
      })
    : orderedTabs.filter(tab => {
        if (!tab) return false
        if (tab.id === 'menu-order') return true
        // v4.7.78: ACRES Engagement tab gated to user 106 during pre-rollout testing.
        if (tab.id === 'acres' && !isAcresTestUser) return false
        return !disabledTabs.includes(tab.id)
      })

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050505',
        color: '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid transparent',
            borderTop: '2px solid #FFD700',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#ffffff' }}>Verifying admin credentials...</p>
        </div>
      </div>
    )
  }

  // 🔒 SECURITY: Only render dashboard if admin is verified
  if (!adminVerified) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050505',
        color: '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#EF4444', fontSize: '64px', marginBottom: '16px' }}>🚫</div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            marginBottom: '8px'
          }}>
            Access Denied
          </h2>
          <p style={{ color: '#ffffff', marginBottom: '16px' }}>
            You do not have admin privileges.
          </p>
          <button
            onClick={onLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#B91C1C'
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#DC2626'
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard-root" style={{
      minHeight: '100vh',
      background: '#050505',
      color: '#FFFFFF',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <AnnouncementPopup />
      {/* Header */}
      <header style={{
        backgroundColor: '#121212',
        borderBottom: '1px solid #1E1E1E',
        backdropFilter: 'blur(16px)',
        boxShadow: '0px 0px 24px rgba(255, 215, 0, 0.15)'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: '#FFD700',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 18px #FFD70066'
                }}>
                  <span style={{ color: '#000000', fontWeight: 'bold', fontSize: '18px' }}>A</span>
                </div>
                <h1 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0
                }}>
                  AUREUS Admin
                </h1>
              </div>
              <span style={{ color: '#4B5563' }}>|</span>
              <span style={{ color: '#ffffff' }}>Content Management System</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ color: '#ffffff' }}>Welcome, {user?.email}</span>

              {/* Debug Status Indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: 'rgba(31, 41, 55, 0.3)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '8px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#10B981',
                  borderRadius: '50%'
                }}></div>
                <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>Debug System</span>
                <span style={{ color: '#ffffff', fontSize: '14px' }}>Available</span>
              </div>

              <button
                onClick={handleLogout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#B91C1C'
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#DC2626'
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '100%', margin: '0 auto', padding: '24px 24px 32px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Sidebar Navigation */}
          <div style={{ width: '240px', flexShrink: 0 }}>
            <nav style={{
              backgroundColor: '#121212',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #1E1E1E',
              backdropFilter: 'blur(16px)',
              boxShadow: '0px 0px 24px rgba(255, 215, 0, 0.15)'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#FFFFFF',
                marginBottom: '16px'
              }}>
                Sections
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(() => {
                  const renderTabTree = (tab: { id: string; name: string; icon: string } | undefined, level: number): React.JSX.Element | null => {
                    if (!tab) return null
                    const isActive = activeTab === tab.id
                    const badgeCount = getBadgeCountForTab(tab.id, notificationCounts)
                    const children = tabs.filter(child => child && menuParents[child.id] === tab.id)
                    const hasChildren = children.length > 0
                    const isExpanded = hasChildren && expandedParents.includes(tab.id)
                    const indent = level * 12

                    const handleClick = () => {
                      console.log('Switching to tab:', tab.id)
                      setActiveTab(tab.id)
                      if (tab.id === 'master-audit') {
                        setMasterAuditHubMode(null)
                        setShowMasterAuditChooser(true)
                      } else {
                        setMasterAuditHubMode(null)
                        setShowMasterAuditChooser(false)
                      }
                      if (hasChildren) {
                        setExpandedParents(prev =>
                          prev.includes(tab.id)
                            ? prev.filter(id => id !== tab.id)
                            : [...prev, tab.id]
                        )
                      }
                    }

                    return (
                      <React.Fragment key={tab.id}>
                        <button
                          onClick={handleClick}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: isActive ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                            border: isActive ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid transparent',
                            color: isActive ? '#FFD700' : '#B0B0B0',
                            marginLeft: indent
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              (e.target as HTMLButtonElement).style.backgroundColor = 'rgba(30, 30, 30, 0.5)';
                              (e.target as HTMLButtonElement).style.color = '#FFFFFF'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                              (e.target as HTMLButtonElement).style.color = '#B0B0B0'
                            }
                          }}
                        >
                          <span style={{ fontSize: '20px' }}>{tab.icon}</span>
                          <span style={{ fontWeight: '500', flex: 1 }}>{tab.name}</span>
                          {hasChildren && (
                            <span
                              style={{
                                fontSize: '14px',
                                color: isActive ? '#FFD700' : '#6B7280',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.15s ease'
                              }}
                            >
                              ▶
                            </span>
                          )}
                          {badgeCount > 0 && (
                            <span style={{
                              backgroundColor: '#EF4444',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              minWidth: '18px',
                              textAlign: 'center',
                              lineHeight: '14px'
                            }}>
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                          )}
                        </button>
                        {hasChildren && isExpanded && children.map(child => renderTabTree(child, level + 1))}
                      </React.Fragment>
                    )
                  }

                  const topLevelTabs = tabs.filter(tab => tab && !menuParents[tab.id])

                  return topLevelTabs.map(tab => renderTabTree(tab, 0)).filter(Boolean)
                })()}
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1 }}>
            {/* v4.7.50: backdropFilter removed from this wrapper — it created
                a containing block for position:fixed descendants which broke
                viewport-centering of every admin modal rendered inside the
                active tab. The blur had no visual effect over the opaque
                #121212 background anyway. */}
            <div style={{
              backgroundColor: '#121212',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #1E1E1E',
              boxShadow: '0px 0px 24px rgba(255, 215, 0, 0.15)'
            }}>
              {activeTab === 'todo' ? (
                <AdminTodoDashboard currentUser={user} />
              ) : activeTab === 'system-settings' ? (
                <SystemSettingsManager currentUser={user} />
              ) : activeTab === 'maintenance' ? (
                <MaintenanceModeManager currentUser={user} />
              ) : activeTab === 'phase-transition' ? (
                <PhaseTransitionManager />
              ) : activeTab === 'gallery' ? (
                <GalleryManager />
              ) : activeTab === 'roadmap-journal' ? (
                <RoadmapJournalManager />
              ) : activeTab === 'how-to-start' ? (
                <AdminHowToStartManager />
              ) : activeTab === 'sponsors' ? (
                <SponsorManager currentUser={user} />
              ) : activeTab === 'gold-diggers-admin' ? (
                <AdminGoldDiggers currentUser={user} />
              ) : activeTab === 'gold-diggers-payouts' ? (
                <AdminPerformancePoolPayouts />
              ) : activeTab === 'competitions' ? (
                <PhaseCompetitionManager />
              ) : activeTab === 'competition-winners' ? (
                <CompetitionWinnersManager currentUser={user} />
              ) : activeTab === 'founding-shareholders' ? (
                <FoundingShareholdersManager />
              ) : activeTab === 'team-management' ? (
                <TeamManager currentUser={user} />
              ) : activeTab === 'meetings' ? (
                <MeetingSchedulerManager />
              ) : activeTab === 'announcements' ? (
                <AnnouncementManager />
              ) : activeTab === 'payments' ? (
                <PaymentManager currentUser={user} />
              ) : activeTab === 'topup-approval' ? (
                <TopupApprovalManager currentUser={user} />
              ) : activeTab === 'share-transfer' ? (
                <AdminShareTransfer currentUser={user} />
              ) : activeTab === 'payment-integrity' ? (
                <PaymentIntegrityAudit />
              ) : activeTab === 'payment-integrity-v2' ? (
                <PaymentIntegrityAuditV2 />
              ) : activeTab === 'reversal-history' ? (
                <ReversalHistory />
              ) : activeTab === 'commissions' ? (
                <CommissionWithdrawalManager currentUser={user} />
              ) : activeTab === 'withdrawal-management' ? (
                <WithdrawalManagementDashboard currentUser={user} />
              ) : activeTab === 'commission-conversions' ? (
                <CommissionConversionManager currentUser={user} />
              ) : activeTab === 'commission-audit' ? (
                <CommissionAuditDashboard />
              ) : activeTab === 'commission-cascade-audit' ? (
                <CommissionCascadeAudit />
              ) : activeTab === 'share-balance-audit' ? (
                <ShareBalanceAuditDashboard />
              ) : activeTab === 'username-audit' ? (
                <UsernameAuditDashboard />
              ) : activeTab === 'master-audit' ? (
                <>
                  <MasterAuditModeChooser
                    open={showMasterAuditChooser}
                    onClose={() => setShowMasterAuditChooser(false)}
                    onSelect={(choice) => {
                      setMasterAuditHubMode(choice)
                      setShowMasterAuditChooser(false)
                    }}
                  />
                  {!masterAuditHubMode && !showMasterAuditChooser ? (
                    <div style={{ color: '#9ca3af', padding: 24 }}>
                      <p style={{ marginBottom: 12 }}>Select a Master Audit tool to continue.</p>
                      <button
                        type="button"
                        onClick={() => setShowMasterAuditChooser(true)}
                        style={{
                          padding: '10px 14px',
                          borderRadius: 6,
                          border: '1px solid #333',
                          background: '#111',
                          color: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        Open Master Audit tools
                      </button>
                    </div>
                  ) : null}
                  {masterAuditHubMode === 'master' ? <MasterAuditEnhanced currentUser={user} /> : null}
                  {masterAuditHubMode === 'remitano' ? (
                    <RemitanoDepositAuditPanel
                      onBack={() => {
                        setMasterAuditHubMode(null)
                        setShowMasterAuditChooser(true)
                      }}
                    />
                  ) : null}
                  {masterAuditHubMode === 'country' ? (
                    <CountryShareAuditPanel
                      onBack={() => {
                        setMasterAuditHubMode(null)
                        setShowMasterAuditChooser(true)
                      }}
                    />
                  ) : null}
                </>
              ) : activeTab === 'financial-audit' ? (
                <FinancialAuditReportGenerator currentUser={user} />
              ) : activeTab === 'invoices' ? (
                <InvoiceManager currentUser={user} />
              ) : activeTab === 'revenue-allocation' ? (
                <RevenueAllocationReport />
              ) : activeTab === 'reports' ? (
                <ShareLedgerReportSystem currentUser={user} />
              ) : activeTab === 'transaction-management' ? (
                <TransactionManagementSystem currentUser={user} />
              ) : activeTab === 'funds' ? (
                <FundManagementDashboard />
              ) : activeTab === 'wallets' ? (
                <AdminWalletManager />
              ) : activeTab === 'certificates' ? (
                <CertificateManagement />
              ) : activeTab === 'cert-test' ? (
                <CertificateTestSystem />
              ) : activeTab === 'users' ? (
                <UserManager currentUser={user} />
              ) : activeTab === 'dormant-purge' ? (
                <DormantAccountPurge />
              ) : activeTab === 'account-suspensions' ? (
                <AccountSuspensionDesk />
              ) : activeTab === 'whatsapp-onboarding' ? (
                <WhatsAppOnboarding currentUser={user} />
              ) : activeTab === 'kyc' ? (
                <KYCManagementDashboard currentUser={user} />
              ) : activeTab === 'profile-kyc-alignment' ? (
                <ProfileKycAlignmentDesk />
              ) : activeTab === 'company-accounts' ? (
                <CompanyManager currentUser={user} />
              ) : activeTab === 'admin-kyc-completion' ? (
                <AdminKYCCompletion currentUser={user} />
              ) : activeTab === 'kyc-cleanup' ? (
                <KYCCleanupDashboard />
              ) : activeTab === 'marketing' ? (
                <MarketingMaterialsManager />
              ) : activeTab === 'list-manager' ? (
                <ListManagerDashboard currentUser={user} />
              ) : activeTab === 'country-manager' ? (
                <CountryManager currentUser={user} />
              ) : activeTab === 'mass-email' ? (
                <MassEmailBroadcast currentUser={user} />
              ) : activeTab === 'menu-order' ? (
                <MenuOrderManager
                  currentUser={user}
                  menuItems={defaultTabs}
                  onOrderChange={handleMenuOrderChange}
                  onConfigChange={handleMenuConfigChange}
                />
              ) : activeTab === 'audit' ? (
                <AuditLogViewer />
              ) : activeTab === 'admin-users' ? (
                <AdminUserManager currentUser={user} />
              ) : activeTab === 'role-permissions' ? (
                <AdminRolePermissionManager currentUser={user} />
              ) : activeTab === 'user-assistance' ? (
                <UserAssistance currentUser={user} />
              ) : activeTab === 'login-lockouts' ? (
                <LoginLockoutManager />
              ) : activeTab === 'my-security' ? (
                <AdminSecurity currentUser={user} />
              ) : activeTab === 'session-manager' ? (
                <SessionManager currentUser={user} />
              ) : activeTab === 'locked-wallets' ? (
                <LockedWalletAdmin />
              ) : activeTab === 'security-admin' ? (
                <SecurityAdminDashboard currentUser={user} />
              ) : activeTab === 'acres' ? (
                // v4.7.78: ACRES admin surface is restricted to user 106 during
                // pre-rollout testing. The tabs filter above already hides this
                // entry for non-106 admins; this render guard is defense-in-depth
                // in case the tab is reached via deep-link / hash navigation.
                isAcresTestUser ? (
                  <AcresDashboard currentUser={user} />
                ) : (
                  <div style={{ padding: 24, background: 'rgba(220,38,38,0.08)', border: '1px solid #DC2626', borderRadius: 8, color: '#FCA5A5' }}>
                    ACRES Engagement is currently restricted to the pre-rollout test account.
                  </div>
                )
              ) : activeTab === 'support' ? (
                <ComprehensiveSupportDashboard currentUser={user?.adminUser} />
              ) : activeTab === 'training' ? (
                <TrainingAcademyManager currentUser={user} />
              ) : activeTab === 'test-audit' ? (
                <TestUserAuditReport />
              ) : activeTab === 'phase-test' ? (
                <div style={{
                  padding: '24px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  color: '#BFDBFE'
                }}>
                  Phase commission diagnostics are currently unavailable in this deployment build.
                </div>
              ) : activeTab === 'debug' ? (
                <div>
                  <h3 style={{
                    fontSize: '20px',
                    color: '#F59E0B',
                    marginBottom: '24px',
                    fontWeight: '600'
                  }}>
                    🐛 Debug Panel
                  </h3>
                  <p style={{ color: '#ffffff', marginBottom: '16px' }}>
                    Current active tab: {activeTab}
                  </p>
                  <div style={{
                    backgroundColor: 'rgba(31, 41, 55, 0.5)',
                    borderRadius: '8px',
                    border: '1px solid #374151',
                    padding: '24px'
                  }}>
                    <h4 style={{
                      color: '#FFFFFF',
                      fontSize: '18px',
                      marginBottom: '16px',
                      fontWeight: '600'
                    }}>
                      Debug Panel is Working!
                    </h4>
                    <p style={{ color: '#ffffff', marginBottom: '16px' }}>
                      If you can see this, the debug tab is functioning correctly.
                    </p>
                    <div style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '16px'
                    }}>
                      <p style={{ color: '#60A5FA', fontSize: '14px' }}>
                        <strong>Next:</strong> Database connection test will be loaded here.
                      </p>
                    </div>
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <DatabaseTest />
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <h3 style={{
                    fontSize: '20px',
                    color: '#F59E0B',
                    marginBottom: '16px',
                    fontWeight: '600'
                  }}>
                    📝 Unknown Section
                  </h3>
                  <p style={{ color: '#ffffff' }}>
                    Section not found: <span style={{ color: '#FCD34D' }}>{activeTab}</span>
                  </p>
                  <p style={{ color: '#ffffff', marginTop: '8px' }}>
                    Please select a valid section from the sidebar.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
